// ---------------------------------------------------------------------------
// C1: Persistent AI Bar — Visual Shell
// ---------------------------------------------------------------------------
// Fixed bottom of viewport, 560px max-width, centered. Glass effect.
// Sparkle icon (pulsable), text input, mic icon, ⌘K hint.
// Gradient border on focus. z-50. Mounted in DashboardLayout.
// ---------------------------------------------------------------------------
import { useState, useRef, useCallback, useEffect, useId, type FormEvent } from "react";
import { Sparkles, Mic, MicOff, X } from "lucide-react";
import { Skeleton } from "@/components/skeleton";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AiBarMode = "idle" | "ask" | "advise" | "do";

interface AiBarProps {
  /** Whether the sparkle icon should pulse (proactive suggestion ready) */
  isPulsing?: boolean;
  /** Callback when user submits a query */
  onSubmit?: (query: string) => void;
  /** Callback when pulse icon is clicked */
  onPulseClick?: () => void;
  /** Current mode */
  mode?: AiBarMode;
  /** Whether the bar is processing a request */
  isProcessing?: boolean;
  /** Whether the bar should stay visible but non-interactive */
  isDisabled?: boolean;
  /** Placeholder shown while the runtime is unavailable */
  disabledPlaceholder?: string;
  /** Children rendered in the expandable panel above the bar */
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Voice input hook (Web Speech API with graceful degradation)
// ---------------------------------------------------------------------------

function useVoiceInput(onResult: (transcript: string) => void) {
  const [isListening, setIsListening] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recognitionRef = useRef<any>(null);

  const isSupported = typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const startListening = useCallback(() => {
    if (!isSupported) return;

    // Web Speech API — types not in standard TS lib, use runtime detection
    const win = window as unknown as Record<string, unknown>;
    const SpeechRecognitionApi = (win.SpeechRecognition ?? win.webkitSpeechRecognition) as { new(): any };
    const recognition = new SpeechRecognitionApi();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = (event: { results: Array<Array<{ transcript: string }>> }) => {
      const transcript = event.results[0]?.[0]?.transcript ?? "";
      if (transcript) onResult(transcript);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isSupported, onResult]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  return { isListening, isSupported, startListening, stopListening };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AiBar({
  isPulsing = false,
  onSubmit,
  onPulseClick,
  mode = "idle",
  isProcessing = false,
  isDisabled = false,
  disabledPlaceholder = "AI assistant unavailable.",
  children,
}: AiBarProps) {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  const handleVoiceResult = useCallback((transcript: string) => {
    setQuery(transcript);
    onSubmit?.(transcript);
  }, [onSubmit]);

  const voice = useVoiceInput(handleVoiceResult);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || isProcessing || isDisabled) return;
    onSubmit?.(trimmed);
  };

  const handleClear = () => {
    setQuery("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    const focusHandler = () => {
      if (isDisabled || isProcessing) {
        return;
      }
      inputRef.current?.focus();
    };

    window.addEventListener("ease:focus-ai-bar", focusHandler);
    return () => window.removeEventListener("ease:focus-ai-bar", focusHandler);
  }, [isDisabled, isProcessing]);

  const hasPanel = !!children && mode !== "idle";

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pb-4 px-4 pointer-events-none">
      <div className="w-full max-w-[560px] pointer-events-auto">
        {/* Expandable panel (renders above bar) */}
        {hasPanel && (
          <div className="mb-2 rounded-xl border border-border bg-background/95 shadow-lg backdrop-blur-xl overflow-hidden animate-in slide-in-from-bottom-2 duration-200">
            {children}
          </div>
        )}

        {/* AI Bar */}
        <form
          onSubmit={handleSubmit}
          className={`
            relative flex items-center gap-2 rounded-2xl px-4 py-3
            bg-background/80 backdrop-blur-[12px]
            border transition-all duration-200
            shadow-lg
            ${isFocused
              ? "border-transparent ring-1 ring-[var(--color-primary)]/40 shadow-[0_0_20px_rgba(16,64,204,0.15)]"
              : "border-border/60"
            }
          `}
          style={isFocused ? {
            backgroundImage: "linear-gradient(var(--color-background), var(--color-background)), var(--gradient-brand)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          } : undefined}
        >
          {/* Sparkle icon (pulsable) */}
          <button
            type="button"
            onClick={onPulseClick}
            className={`
              flex-shrink-0 rounded-lg p-1.5 transition-all
              ${isPulsing
                ? "text-[var(--color-ai)] animate-[ai-pulse_2s_ease-in-out_infinite]"
                : "text-muted-foreground hover:text-[var(--color-ai)]"
              }
            `}
            aria-label={isPulsing ? "AI has a suggestion" : "AI assistant"}
            disabled={isDisabled}
          >
            <Sparkles className="h-5 w-5" />
          </button>

          {/* Text input */}
          <label htmlFor={inputId} className="sr-only">Ask AI anything</label>
          {isProcessing ? (
            <div aria-hidden="true" className="flex flex-1 items-center gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-40" />
            </div>
          ) : (
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={
                isDisabled
                  ? disabledPlaceholder
                  : "Ask anything, get advice, or say \"do it\"..."
              }
              disabled={isDisabled}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
              autoComplete="off"
            />
          )}

          {/* Clear button (when has text) */}
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Mic button (if supported) */}
          {voice.isSupported && (
            <button
              type="button"
              onClick={voice.isListening ? voice.stopListening : voice.startListening}
              className={`flex-shrink-0 rounded-lg p-1.5 transition-colors ${
                voice.isListening
                  ? "text-[var(--color-destructive)] bg-[var(--color-destructive)]/5"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label={voice.isListening ? "Stop listening" : "Voice input"}
              disabled={isDisabled}
            >
              {voice.isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}

          {/* ⌘K hint */}
          <kbd className="flex-shrink-0 hidden sm:inline-flex items-center gap-0.5 rounded border border-border/60 px-1.5 py-0.5 text-[10px] text-muted-foreground/60">
            ⌘K
          </kbd>

          {/* Processing indicator */}
          {isProcessing && (
            <div className="absolute bottom-0 left-4 right-4 h-0.5 overflow-hidden rounded-full">
              <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-ai)] animate-[shimmer_1.5s_ease-in-out_infinite]" />
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
